import {ROOT_STORAGE_WEB} from 'helpers/config'
import {downloadBase64File, downloadBlob} from 'utils/exportUtils'

export class OfferPdfDownloadError extends Error {
    constructor(public readonly messageKey: string, public readonly status?: number) {
        super(status ? `${messageKey} (status ${status})` : messageKey)
    }
}

const isPdf = (bytes: Uint8Array) => bytes.length >= 5 && String.fromCharCode(...bytes.slice(0, 5)) === '%PDF-'

/** URL exports are handed to the browser; base64 exports are verified before downloading. */
export const resolveOfferPdfDownload = async (fileData?: string | null, filename = 'document.pdf', preview?: Window | null): Promise<boolean> => {
    const value = fileData?.trim()
    if (!value) return false
    if (/^https?:\/\//i.test(value) || value.startsWith('/')) {
        let url = new URL(value, ROOT_STORAGE_WEB)
        // Some API deployments generate storage URLs using their internal APP_URL.
        // Resolve only their PDF storage path against the configured public backend;
        // never request the user's loopback server or an arbitrary external address.
        if (
            ['127.0.0.1', 'localhost', '[::1]'].includes(url.hostname) &&
            !url.username && !url.password &&
            /^\/storage\/uploads\/pdf\/[^/]+\.pdf$/i.test(url.pathname)
        ) {
            url = new URL(`${url.pathname}${url.search}`, ROOT_STORAGE_WEB)
        }
        if (url.origin !== new URL(ROOT_STORAGE_WEB).origin || url.username || url.password) {
            throw new OfferPdfDownloadError('The server returned an unavailable PDF address. Please contact support.')
        }
        if (preview !== undefined) {
            if (!preview || preview.closed) throw new OfferPdfDownloadError('Allow pop-ups to open the exported PDF, then try again.')
            // Navigation can display PDFs even when storage does not grant CORS access.
            // The window was reserved synchronously, before the export API request.
            preview.location.replace(url.href)
            return true
        }
        let response: Response
        try {
            response = await fetch(url.href, {credentials: 'omit', redirect: 'error'})
        } catch {
            throw new OfferPdfDownloadError('PDF download failed. Please try again.')
        }
        if (!response.ok) throw new OfferPdfDownloadError('PDF download failed. Please try again.', response.status)
        const bytes = new Uint8Array(await response.arrayBuffer())
        if (!isPdf(bytes)) throw new OfferPdfDownloadError('The server did not return a PDF document.')
        downloadBlob(new Blob([bytes], {type: 'application/pdf'}), filename)
        return true
    }
    preview?.close()
    const base64 = value.replace(/^data:application\/pdf;base64,/i, '').replace(/\s+/g, '')
    try {
        if (!isPdf(Uint8Array.from(atob(base64), character => character.charCodeAt(0)))) return false
    } catch { return false }
    downloadBase64File(base64, filename)
    return true
}
